import type { ChatMessage } from '@/components/chat/Chat';

export interface MessageGroup {
  date: Date;
  messages: ChatMessage[];
  messageCount: number;
}

export function groupMessagesByDate(messages: ChatMessage[]): MessageGroup[] {
  const groups: { [key: string]: MessageGroup } = {};

  messages.forEach((message) => {
    const messageDate = new Date(message.timestamp);
    const dateKey = messageDate.toDateString(); // Use date string as key to group by day

    if (!groups[dateKey]) {
      groups[dateKey] = {
        date: new Date(messageDate.getFullYear(), messageDate.getMonth(), messageDate.getDate()),
        messages: [],
        messageCount: 0,
      };
    }

    groups[dateKey].messages.push(message);
    groups[dateKey].messageCount++;
  });

  // Convert to array and sort by date (oldest first)
  return Object.values(groups).sort((a, b) => a.date.getTime() - b.date.getTime());
}

export function shouldShowDayBreak(currentMessage: ChatMessage, previousMessage?: ChatMessage, allMessages?: ChatMessage[]): boolean {
  if (!previousMessage) {
    // If this is the first message, check if all messages are from today
    if (allMessages && allMessages.length > 0) {
      const today = new Date();
      const allFromToday = allMessages.every(message => {
        const messageDate = new Date(message.timestamp);
        return messageDate.getUTCFullYear() === today.getUTCFullYear()
          && messageDate.getUTCMonth() === today.getUTCMonth()
          && messageDate.getUTCDate() === today.getUTCDate();
      });
      
      // Don't show day break if all messages are from today
      if (allFromToday) {
        return false;
      }
    }
    return true;
  }
  
  const currentDate = new Date(currentMessage.timestamp);
  const previousDate = new Date(previousMessage.timestamp);

  return currentDate.getUTCFullYear() !== previousDate.getUTCFullYear()
    || currentDate.getUTCMonth() !== previousDate.getUTCMonth()
    || currentDate.getUTCDate() !== previousDate.getUTCDate();
}

/**
 * Worker chat often pairs "View Report" (opens report builder) with a separate
 * "Download PDF" action. Omit the PDF glyph on the view row for that label so
 * the type is not implied twice; other view labels still show the file icon.
 *
 * @param label - `actions[].label` for a `type: "view"` action
 * @returns Whether to render the PDF icon in the view action control
 */
export function shouldShowPdfIconOnViewAction(label: string): boolean {
  return label.trim().toLowerCase() !== "view report";
} 